/**
 * Piezas compartidas de la interfaz. Sin lógica de negocio y sin depender de
 * ninguna feature (constitución, Art. 8).
 *
 * Todas visten la identidad de GuardIA: superficie blanca sobre azul muy claro,
 * radios amplios, sombra suave, cian para el foco y azul oscuro para el texto
 * de peso. Los estados no se comunican solo con color — cada uno lleva su
 * palabra — porque un informe que solo se entiende distinguiendo tonos deja
 * afuera a quien no los distingue.
 */

import type { ReactNode } from "react";

export function Card({
  title,
  hint,
  actions,
  children,
}: {
  title?: string;
  hint?: string;
  /** Botones o filtros propios de la tarjeta, alineados con el título. */
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="gd-card p-5 sm:p-7">
      {title ? (
        <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-display text-lg font-bold tracking-tight text-gd-navy">{title}</h2>
            {hint ? <p className="mt-1 text-sm text-gd-muted">{hint}</p> : null}
          </div>
          {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
        </header>
      ) : null}
      {children}
    </section>
  );
}

/**
 * Cifra grande con su unidad y, cuando hace falta, la advertencia de por qué
 * ese número puede no significar lo que parece.
 */
export function Stat({
  label,
  value,
  unit,
  caveat,
}: {
  label: string;
  value: string | number | null;
  unit?: string;
  caveat?: string;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-sm font-medium text-gd-muted">{label}</dt>
      <dd className="mt-1 font-display text-3xl font-bold tabular-nums tracking-tight text-gd-navy">
        {value === null ? (
          <span className="text-base font-normal text-gd-muted">sin dato</span>
        ) : (
          <>
            {value}
            {unit ? <span className="ml-1 text-base font-medium text-gd-muted">{unit}</span> : null}
          </>
        )}
      </dd>
      {caveat ? (
        <p className="mt-1.5 text-sm font-medium text-amber-700">{caveat}</p>
      ) : null}
    </div>
  );
}

/**
 * Aviso. `tone="warning"` es para cuando una cifra puede leerse al revés de lo
 * que significa — el caso del cero por falta de muestreo.
 */
export function Notice({
  tone = "info",
  title,
  children,
}: {
  tone?: "info" | "warning";
  title: string;
  children: ReactNode;
}) {
  const warning = tone === "warning";
  return (
    <div
      className={`flex gap-3 rounded-[18px] border p-4 sm:p-5 ${
        warning
          ? "border-amber-300 bg-amber-50 text-amber-950"
          : "border-gd-line bg-white text-gd-ink shadow-[var(--shadow-card)]"
      }`}
      role={warning ? "alert" : undefined}
    >
      {/* El icono acompaña al texto; nunca lo reemplaza. */}
      <span
        aria-hidden="true"
        className={`mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full text-[13px] font-bold ${
          /* Azul y no cian: blanco sobre cian da 2,5:1 y no se lee. */
          warning ? "bg-amber-400 text-amber-950" : "bg-gd-blue text-white"
        }`}
      >
        {warning ? "!" : "i"}
      </span>
      <div className="min-w-0">
        <p className="font-display text-[15px] font-bold text-inherit">{title}</p>
        <div className="mt-1 text-[15px] leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

const SEVERITY_STYLE: Record<string, string> = {
  low: "bg-gd-mist text-gd-navy ring-gd-line",
  moderate: "bg-amber-100 text-amber-900 ring-amber-300",
  high: "bg-orange-100 text-orange-900 ring-orange-300",
  critical: "bg-red-100 text-red-900 ring-red-300",
  inconclusive: "bg-gd-mist text-gd-muted ring-gd-line",
};

const SEVERITY_LABEL: Record<string, string> = {
  low: "Baja",
  moderate: "Moderada",
  high: "Alta",
  critical: "Crítica",
  inconclusive: "Inconclusa",
};

/** La severidad se nombra, no solo se colorea: se lee sin distinguir tonos. */
export function SeverityTag({ severity }: { severity: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-[13px] font-semibold ring-1 ring-inset ${
        SEVERITY_STYLE[severity] ?? SEVERITY_STYLE.inconclusive
      }`}
    >
      {SEVERITY_LABEL[severity] ?? severity}
    </span>
  );
}

/* --------------------------------------------------------------- *
 * Controles
 * --------------------------------------------------------------- */

type ButtonProps = {
  variant?: "primary" | "secondary";
  /** En celular ocupa todo el ancho: es más fácil de acertar con el pulgar. */
  block?: boolean;
  children: ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

export function Button({
  variant = "primary",
  block = false,
  className = "",
  children,
  ...rest
}: ButtonProps) {
  const base = variant === "primary" ? "gd-btn-primary" : "gd-btn-secondary";
  return (
    <button
      {...rest}
      className={`${base} ${block ? "w-full sm:w-auto" : ""} ${className}`.trim()}
    >
      {children}
    </button>
  );
}

/**
 * Campo con etiqueta visible y, si hace falta, su error debajo.
 *
 * La etiqueta es un elemento propio y no un placeholder: el placeholder se
 * borra al escribir y deja el formulario sin rótulos justo cuando hay que
 * revisarlo. El error se ata al control con `aria-describedby` para que un
 * lector de pantalla lo anuncie y no quede solo como texto rojo.
 */
export function Field({
  label,
  htmlFor,
  hint,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0 flex-1">
      <label className="gd-label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {hint && !error ? (
        <p className="mt-1.5 text-[13px] text-gd-muted" id={`${htmlFor}-hint`}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="mt-1.5 text-[13px] font-medium text-red-700" id={`${htmlFor}-error`}>
          {error}
        </p>
      ) : null}
    </div>
  );
}
